import { useState, useEffect } from "react";

/**
 * Binance-style Color Preferences for Spot Trading:
 * 1. Traditional: Emerald Green (Bullish / Buy) & Crimson Red (Bearish / Sell) [settingId: 21]
 * 2. Fresh (Default): Neon Green (Buy/Up) & Hot Pink/Red (Sell/Down) [settingId: 22]
 * 3. Color vision deficiency: Sky Blue (Buy/Up) & Vivid Amber/Orange (Sell/Down) [settingId: 23]
 */
export const COLOR_PREFERENCES = {
  TRADITIONAL: {
    id: "TRADITIONAL",
    settingId: 21,
    label: "Emerald & Crimson",
    name: "Emerald & Crimson",
    upColor: "#0ECB81",    // Green (Bullish / Buy)
    downColor: "#F6465D",  // Red / Coral (Bearish / Sell)
    upBadge: "#0ECB81",
    downBadge: "#F6465D",
    upBg: "rgba(14, 203, 129, 0.15)",
    downBg: "rgba(246, 70, 93, 0.15)",
    upBorder: "rgba(14, 203, 129, 0.4)",
    downBorder: "rgba(246, 70, 93, 0.4)",
    pairButtonColor: "#0ECB81", // Emerald Green
    pairButtonHover: "#0ba86b",
  },
  FRESH: {
    id: "FRESH",
    settingId: 22,
    label: "Neon Green & Crimson",
    name: "Neon Green & Crimson",
    upColor: "#00FF00",    // Neon / Lime Green
    downColor: "#FF0000",  // Hot Pink / Vivid Magenta
    upBadge: "#00FF00",
    downBadge: "#FF0000",
    upBg: "rgba(0, 255, 0, 0.15)",
    downBg: "rgba(250, 34, 86, 0.15)",
    upBorder: "rgba(0, 255, 0, 0.4)",
    downBorder: "rgba(250, 34, 86, 0.4)",
    pairButtonColor: "#00C853", // Vibrant Fresh Green matching neon theme with high legibility
    pairButtonHover: "#00b248",
  },
  COLOR_VISION_DEFICIENCY: {
    id: "COLOR_VISION_DEFICIENCY",
    settingId: 23,
    label: "Electric Blue & Tangerine",
    name: "Electric Blue & Tangerine",
    upColor: "#3888FF",    // Sky Blue
    downColor: "#F6851B",  // Vivid Amber / Orange
    upBadge: "#3888FF",
    downBadge: "#F6851B",
    upBg: "rgba(56, 136, 255, 0.15)",
    downBg: "rgba(246, 133, 27, 0.15)",
    upBorder: "rgba(56, 136, 255, 0.4)",
    downBorder: "rgba(246, 133, 27, 0.4)",
    pairButtonColor: "#3888FF", // Electric Sky Blue
    pairButtonHover: "#2570ea",
  },
};

export const COLOR_PREFERENCE_LIST = [
  COLOR_PREFERENCES.TRADITIONAL,
  COLOR_PREFERENCES.FRESH,
  COLOR_PREFERENCES.COLOR_VISION_DEFICIENCY,
];

const STORAGE_KEY = "spot_color_preference";
const EVENT_NAME = "spot_trading_color_change";

/**
 * Apply CSS variables to root document
 */
export const applyTradingColorCssVars = (palette) => {
  if (typeof document === "undefined" || !palette) return;
  const root = document.documentElement;
  root.style.setProperty("--trading-up-color", palette.upColor);
  root.style.setProperty("--trading-down-color", palette.downColor);
  root.style.setProperty("--trading-up-bg", palette.upBg);
  root.style.setProperty("--trading-down-bg", palette.downBg);
  root.style.setProperty("--trading-up-border", palette.upBorder);
  root.style.setProperty("--trading-down-border", palette.downBorder);
  if (palette.pairButtonColor) {
    root.style.setProperty("--trading-pair-btn-color", palette.pairButtonColor);
  }
  if (palette.pairButtonHover) {
    root.style.setProperty("--trading-pair-btn-hover", palette.pairButtonHover);
  }
};

/**
 * Read current active palette from localStorage (defaults to FRESH)
 */
export const getTradingColorPreference = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && COLOR_PREFERENCES[saved]) {
      return COLOR_PREFERENCES[saved];
    }
  } catch (e) {
    console.error("Error reading spot color preference:", e);
  }
  return COLOR_PREFERENCES.FRESH;
};

/**
 * Update color preference, save to localStorage, apply CSS vars and broadcast event
 */
export const setTradingColorPreference = (preferenceId) => {
  const target = COLOR_PREFERENCES[preferenceId] || COLOR_PREFERENCES.FRESH;
  try {
    localStorage.setItem(STORAGE_KEY, target.id);
  } catch (e) {
    console.error("Error saving spot color preference:", e);
  }
  applyTradingColorCssVars(target);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: target }));
  }
  return target;
};

/**
 * Hook to use trading colors reactively in any React component
 */
export const useTradingColors = () => {
  const [currentPalette, setCurrentPalette] = useState(() => {
    const pal = getTradingColorPreference();
    applyTradingColorCssVars(pal);
    return pal;
  });

  useEffect(() => {
    const handleColorChange = (e) => {
      if (e?.detail) {
        setCurrentPalette(e.detail);
      } else {
        setCurrentPalette(getTradingColorPreference());
      }
    };

    window.addEventListener(EVENT_NAME, handleColorChange);
    return () => {
      window.removeEventListener(EVENT_NAME, handleColorChange);
    };
  }, []);

  return {
    ...currentPalette,
    preference: currentPalette.id,
    setPreference: setTradingColorPreference,
  };
};
